import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Copy, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/app/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/app/components/ui/table';
import { toast } from '@/app/components/ui/simple-toaster';
import { useNavigate } from 'react-router-dom';
import { useAssetTemplates, useCreateAssetTemplate, useUpdateAssetTemplate, useDeleteAssetTemplate } from '@/hooks/use-settings';
import { Skeleton } from '@/app/components/ui/skeleton';
import type { AssetTemplate, AssetType } from '@/lib/types';

const ASSET_TYPE_OPTIONS: { value: AssetType; label: string }[] = [
  { value: 'carabiner', label: 'Carabiner' },
  { value: 'harness', label: 'Harness' },
  { value: 'rope', label: 'Rope' },
  { value: 'lanyard', label: 'Lanyard' },
  { value: 'sling', label: 'Sling' },
  { value: 'descender', label: 'Descender' },
  { value: 'ascender', label: 'Ascender' },
  { value: 'pulley', label: 'Pulley' },
  { value: 'helmet', label: 'Helmet' },
  { value: 'anchor', label: 'Anchor' },
  { value: 'connector', label: 'Connector' },
  { value: 'fall_arrester', label: 'Fall Arrester' },
  { value: 'other', label: 'Other' },
];

export function AssetTemplatesPage() {
  const navigate = useNavigate();
  const { data: templatesData, isLoading, error } = useAssetTemplates();
  const createTemplate = useCreateAssetTemplate();
  const updateTemplate = useUpdateAssetTemplate();
  const deleteTemplate = useDeleteAssetTemplate();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AssetTemplate | null>(null);
  
  // Form state
  const [type, setType] = useState<AssetType>('other');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [dateOfManufacture, setDateOfManufacture] = useState('');
  const [length, setLength] = useState('');
  const [safeWorkingLoad, setSafeWorkingLoad] = useState('');
  const [inspectionInterval, setInspectionInterval] = useState('90');

  const resetForm = () => {
    setType('other');
    setManufacturer('');
    setModel('');
    setDateOfManufacture('');
    setLength('');
    setSafeWorkingLoad('');
    setInspectionInterval('90');
    setEditingTemplate(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const payload = {
      type,
      manufacturer,
      model,
      date_of_manufacture: dateOfManufacture || undefined,
      length_m: length || undefined,
      safe_working_load: safeWorkingLoad || undefined,
      inspection_interval_days: parseInt(inspectionInterval, 10) || undefined,
    };

    try {
      if (editingTemplate) {
        await updateTemplate.mutateAsync({ id: editingTemplate.id, data: payload });
        toast.success('Template Updated', {
          description: `${name} template has been updated.`,
        });
      } else {
        await createTemplate.mutateAsync(payload);
        toast.success('Template Created', {
          description: `${name} template has been created.`,
        });
      }
      resetForm();
      setIsCreateDialogOpen(false);
    } catch (_err) {
      toast.error('Error', {
        description: `Failed to ${editingTemplate ? 'update' : 'create'} template. Please try again.`,
      });
    }
  };

  const handleEdit = (template: AssetTemplate) => {
    setEditingTemplate(template);
    setType(template.type || 'other');
    setManufacturer(template.manufacturer);
    setModel(template.model);
    setDateOfManufacture(template.date_of_manufacture || '');
    setLength(template.length_m?.toString() || '');
    setSafeWorkingLoad(template.safe_working_load?.toString() || '');
    setInspectionInterval(template.inspection_interval_days?.toString() || '90');
    setIsCreateDialogOpen(true);
  };

  const handleDelete = async (template: AssetTemplate) => {
    if (!confirm(`Are you sure you want to delete the "${template.display_name}" template?`)) return;
    try {
      await deleteTemplate.mutateAsync(template.id);
      toast.success('Template Deleted', {
        description: `${template.display_name} template has been removed.`,
      });
    } catch (_err) {
      toast.error('Error', {
        description: 'Failed to delete template. Please try again.',
      });
    }
  };

  const handleCreateAssetFromTemplate = (template: AssetTemplate) => {
    navigate('/assets/new', { state: { template } });
  };

  const templates = templatesData?.results || [];

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Error Loading Templates</h3>
                <p className="text-sm text-red-800 mt-1">
                  Failed to load asset templates. Please try refreshing the page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Asset Templates</h1>
          <p className="text-gray-600 mt-1">Create reusable templates for quick asset creation</p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Asset Template'}</DialogTitle>
              <DialogDescription>
                Define default values for quickly creating similar assets
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Type *</Label>
                  <Select value={type} onValueChange={(value) => setType(value as AssetType)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select asset type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSET_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="manufacturer">Manufacturer *</Label>
                    <Input
                      id="manufacturer"
                      placeholder="e.g., Petzl"
                      value={manufacturer}
                      onChange={(e) => setManufacturer(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="model">Model *</Label>
                    <Input
                      id="model"
                      placeholder="e.g., Vertex Vent"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date-of-manufacture">Date of Manufacture</Label>
                    <Input
                      id="date-of-manufacture"
                      type="date"
                      value={dateOfManufacture}
                      onChange={(e) => setDateOfManufacture(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="length">Default Length (m)</Label>
                    <Input
                      id="length"
                      type="number"
                      step="0.1"
                      placeholder="e.g., 50"
                      value={length}
                      onChange={(e) => setLength(e.target.value)}
                    />
                    <p className="text-xs text-gray-500">Optional - for rope assets</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="swl">Safe Working Load (kg)</Label>
                    <Input
                      id="swl"
                      type="number"
                      placeholder="e.g., 2200"
                      value={safeWorkingLoad}
                      onChange={(e) => setSafeWorkingLoad(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inspection-interval">Inspection Interval (days)</Label>
                    <Input
                      id="inspection-interval"
                      type="number"
                      placeholder="e.g., 90"
                      value={inspectionInterval}
                      onChange={(e) => setInspectionInterval(e.target.value)}
                    />
                    <p className="text-xs text-gray-500">How often this asset should be inspected</p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => {
                  resetForm();
                  setIsCreateDialogOpen(false);
                }}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Templates List */}
      <Card>
        <CardHeader>
          <CardTitle>Saved Templates</CardTitle>
          <CardDescription>
            Quickly create new assets using these predefined templates
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Length</TableHead>
                  <TableHead className="text-right">SWL</TableHead>
                  <TableHead className="text-right">Inspection</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      No templates created yet. Create your first template to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.display_name}</TableCell>
                      <TableCell className="capitalize">{template.type?.replace('_', ' ') || '-'}</TableCell>
                      <TableCell className="text-right">
                        {template.length_m ? `${template.length_m}m` : '-'}
                      </TableCell>
                      <TableCell className="text-right">{template.safe_working_load ? `${template.safe_working_load} kg` : '-'}</TableCell>
                      <TableCell className="text-right">{template.inspection_interval_days ? `${template.inspection_interval_days} days` : '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCreateAssetFromTemplate(template)}
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Use
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(template)}
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(template)}
                            disabled={deleteTemplate.isPending}
                          >
                            <Trash2 className="h-3 w-3 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
